/**
 * Formatting for the Performance page (gold v2 phase 4b). Formatting only:
 * every number arrives computed from the backend. Unknown (null) -> "—".
 */

export const DASH = '—';

/** +$1,234 / -$56 (whole dollars). */
export const fmtUsd = (v: number | null | undefined): string => {
  if (v == null) return DASH;
  const r = Math.round(v);
  return `${r >= 0 ? '+' : '-'}$${Math.abs(r).toLocaleString('en-US')}`;
};

/** $1.2k / $850 / $1.20M without a sign (magnitudes). */
export const fmtUsdShort = (v: number | null | undefined): string => {
  if (v == null) return DASH;
  const a = Math.abs(v);
  if (a >= 1_000_000) return `$${(a / 1_000_000).toFixed(2)}M`;
  if (a >= 1000) return `$${(a / 1000).toFixed(1)}k`;
  return `$${a.toFixed(0)}`;
};

/** Signed short dollars: +$1.2k / -$850. */
export const fmtUsdShortSigned = (v: number | null | undefined): string => {
  if (v == null) return DASH;
  return `${v >= 0 ? '+' : '-'}${fmtUsdShort(v)}`;
};

/** A fraction as a signed percent: 0.0523 -> +5.23%. */
export const fmtPct = (v: number | null | undefined, digits = 2): string => {
  if (v == null) return DASH;
  const p = v * 100;
  return `${p >= 0 ? '+' : ''}${p.toFixed(digits)}%`;
};

/** A fraction as an unsigned percent: 0.6 -> 60%. */
export const fmtShare = (v: number | null | undefined, digits = 0): string => {
  if (v == null) return DASH;
  return `${(v * 100).toFixed(digits)}%`;
};

export const fmtRatio = (v: number | null | undefined, digits = 2, suffix = 'x'): string => {
  if (v == null) return DASH;
  return `${v.toFixed(digits)}${suffix}`;
};

export const fmtNum = (v: number | null | undefined, digits = 0): string => {
  if (v == null) return DASH;
  return v.toFixed(digits);
};

/** Colour class for a signed value; unknown and zero read neutral. */
export const toneOf = (v: number | null | undefined): 'is-gain' | 'is-loss' | '' => {
  if (v == null || v === 0) return '';
  return v > 0 ? 'is-gain' : 'is-loss';
};

/** Legacy table classes (positive-pnl / negative-pnl / neutral-pnl). */
export const pnlClass = (v: number | null | undefined): string => {
  if (v == null || v === 0) return 'neutral-pnl';
  return v > 0 ? 'positive-pnl' : 'negative-pnl';
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-06" -> parts without a timezone shift (the dates are New York dates). */
const parts = (iso: string): [number, number, number] => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return [y, m, d];
};

/** "Oct 6, 2026" */
export const fmtDay = (iso: string | null | undefined): string => {
  if (!iso) return DASH;
  const [y, m, d] = parts(iso);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};

/** "Oct 6" */
export const fmtDayShort = (iso: string | null | undefined): string => {
  if (!iso) return DASH;
  const [, m, d] = parts(iso);
  return `${MONTHS[m - 1]} ${d}`;
};

/** "Oct 2026" / "Oct '26" */
export const fmtMonth = (iso: string, short = false): string => {
  const [y, m] = parts(iso);
  return short ? `${MONTHS[m - 1]} '${String(y).slice(-2)}` : `${MONTHS[m - 1]} ${y}`;
};

/** "Oct 16 '26" */
export const fmtExpiry = (iso: string | null | undefined): string => {
  if (!iso) return DASH;
  const [y, m, d] = parts(iso);
  return `${MONTHS[m - 1]} ${d} '${String(y).slice(-2)}`;
};

/** "Tuesday, Oct 6, 2026" for the "as of" line (dates are New York sessions). */
export const fmtSessionLong = (iso: string | null | undefined): string => {
  if (!iso) return DASH;
  const [y, m, d] = parts(iso);
  const wd = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
  return `${wd}, ${MONTHS[m - 1]} ${d}, ${y}`;
};

export const MONTH_LABELS = MONTHS;
